(function () {
  const successMessage = 'Thanks! We will notify you when it is back.';
  const queuedMessage = "You're already in the queue for this game.";
  const confirmationMessage = 'Check your email to confirm this stock alert.';
  const defaultErrorMessage = 'Could not save that yet. Please try again.';

  function looksLikeEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  function absoluteImageUrl(url) {
    if (!url) return '';
    if (url.startsWith('//')) return `${window.location.protocol}${url}`;
    if (url.startsWith('/')) return `${window.location.origin}${url}`;
    return url;
  }

  function attributeSelectorValue(value) {
    return String(value || '').replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  }

  function safeStorageGet(key) {
    try {
      return window.localStorage.getItem(key) || '';
    } catch (_) {
      return '';
    }
  }

  function safeStorageSet(key, value) {
    try {
      window.localStorage.setItem(key, value);
    } catch (_) {}
  }

  class CavernNotifyMe extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.initialized = true;
      this.form = this.querySelector('form');
      this.emailInput = this.querySelector('input[type="email"]');
      this.button = this.querySelector('button[type="submit"]');
      this.buttonText = this.button?.querySelector('span');
      this.copy = this.querySelector('[data-cavern-notify-copy]');
      this.status = this.querySelector('[data-cavern-notify-status]');

      this.form?.addEventListener('submit', this.handleSubmit.bind(this));
      this.emailInput?.addEventListener('input', this.handleEmailInput.bind(this));
      this.emailInput?.addEventListener('blur', this.checkQueueStatus.bind(this));
      this.updateContent();
      this.ingestConfirmationToken();
      this.checkQueueStatus();

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribeVariantChange = subscribe(
          PUB_SUB_EVENTS.variantChange,
          this.handleVariantChange.bind(this)
        );
      }
    }

    disconnectedCallback() {
      this.unsubscribeVariantChange?.();
    }

    handleVariantChange({ data } = {}) {
      if (!data || data.sectionId !== this.dataset.sectionId || !data.html) return;

      const updated = data.html.querySelector(
        `cavern-notify-me[data-section-id="${attributeSelectorValue(this.dataset.sectionId)}"]`
      );
      if (!updated) return;

      Array.from(this.attributes)
        .filter((attribute) => attribute.name.startsWith('data-'))
        .forEach((attribute) => this.removeAttribute(attribute.name));

      Array.from(updated.attributes)
        .filter((attribute) => attribute.name.startsWith('data-'))
        .forEach((attribute) => this.setAttribute(attribute.name, attribute.value));

      this.classList.toggle('hidden', updated.classList.contains('hidden'));
      this.setQueuedState(false);
      this.setStatus('');
      this.updateContent();
      this.checkQueueStatus();
    }

    updateContent() {
      const isActive = this.dataset.notifyActive === 'true';
      this.classList.toggle('hidden', !isActive);

      if (!this.copy) return;

      this.copy.textContent = 'We will email you when this item comes back in stock.';
    }

    setStatus(message, isError = false) {
      if (!this.status) return;

      this.status.textContent = message;
      this.status.classList.toggle('cavern-notify-me__status--error', Boolean(message && isError));
      this.status.classList.toggle('cavern-notify-me__status--success', Boolean(message && !isError));
    }

    setQueuedState(isQueued) {
      this.isQueued = isQueued;
      this.classList.toggle('cavern-notify-me--queued', isQueued);

      if (isQueued) {
        this.setStatus(queuedMessage);
        if (this.button) this.button.disabled = true;
        if (this.buttonText) this.buttonText.textContent = 'Already in queue';
        return;
      }

      this.resetButton();
    }

    resetButton() {
      if (this.button) this.button.disabled = false;
      if (this.buttonText) this.buttonText.textContent = 'Notify me';
    }

    statusEndpoint() {
      const endpoint = this.dataset.endpoint || '';
      return this.dataset.statusEndpoint || endpoint.replace(/\/notify\/?$/, '/status');
    }

    accessTokenKey(email) {
      const productKey = this.dataset.productGid || this.dataset.productId || this.dataset.productHandle || 'product';
      const variantKey = this.dataset.variantGid || this.dataset.variantId || 'any';
      return `cavernBackInStock:${encodeURIComponent(productKey)}:${encodeURIComponent(variantKey)}:${encodeURIComponent(email)}`;
    }

    productAccessTokenKey(email) {
      const productKey = this.dataset.productGid || this.dataset.productId || this.dataset.productHandle || 'product';
      return `cavernBackInStock:${encodeURIComponent(productKey)}:any:${encodeURIComponent(email)}`;
    }

    storedAccessToken(email) {
      return safeStorageGet(this.accessTokenKey(email)) || safeStorageGet(this.productAccessTokenKey(email));
    }

    rememberAccessToken(email, token) {
      const cleanToken = String(token || '').trim();
      if (!cleanToken) return;
      safeStorageSet(this.accessTokenKey(email), cleanToken);
      safeStorageSet(this.productAccessTokenKey(email), cleanToken);
    }

    ingestConfirmationToken() {
      const params = new URLSearchParams(window.location.search);
      const token = (params.get('bis_token') || '').trim();
      const email = (params.get('bis_email') || '').trim().toLowerCase();
      if (!token || !looksLikeEmail(email)) return;

      this.rememberAccessToken(email, token);
      if (this.emailInput && !this.emailInput.value.trim()) {
        this.emailInput.value = email;
      }
      this.setQueuedState(true);

      params.delete('bis_token');
      params.delete('bis_email');
      const query = params.toString();
      const cleanUrl = `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`;
      window.history.replaceState({}, '', cleanUrl);
    }

    buildPayload(email) {
      const productTitle = this.dataset.productTitle || 'This item';
      const variantTitle = this.dataset.variantTitle || '';
      const accessToken = this.storedAccessToken(email);

      return {
        email,
        ...(accessToken ? { accessToken } : {}),
        productTitle,
        productId: this.dataset.productId || '',
        productGid: this.dataset.productGid || '',
        productHandle: this.dataset.productHandle || '',
        handle: this.dataset.productHandle || '',
        productImage: absoluteImageUrl(this.dataset.productImage || ''),
        notifyScope: 'any_variant',
        variantId: this.dataset.variantId || '',
        requestedVariantId: this.dataset.variantId || '',
        requestedVariantGid: this.dataset.variantGid || '',
        requestedVariantTitle: variantTitle,
        requestedVariantAvailable: this.dataset.variantAvailable === 'true',
        notificationTitle: `${productTitle} is back in stock`,
        notificationBody: `${productTitle} is back in stock. Tap to view it, or ignore this alert to stay on the wait list.`,
        source: 'shopify-theme',
      };
    }

    handleEmailInput() {
      window.clearTimeout(this.queueStatusTimer);
      this.queueStatusRequestId = (this.queueStatusRequestId || 0) + 1;
      this.setQueuedState(false);
      this.setStatus('');

      const email = (this.emailInput?.value || '').trim().toLowerCase();
      if (!looksLikeEmail(email)) return;

      this.queueStatusTimer = window.setTimeout(() => this.checkQueueStatus(), 450);
    }

    async checkQueueStatus() {
      if (this.dataset.notifyActive !== 'true') return;

      const email = (this.emailInput?.value || '').trim().toLowerCase();
      if (!looksLikeEmail(email)) return;
      if (!this.storedAccessToken(email)) return;

      const requestId = (this.queueStatusRequestId || 0) + 1;
      this.queueStatusRequestId = requestId;

      try {
        const response = await fetch(this.statusEndpoint(), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(this.buildPayload(email)),
        });
        const data = await response.json().catch(() => ({}));
        if (this.queueStatusRequestId !== requestId) return;

        if (response.ok && data.success !== false && (data.queued || data.alreadySubscribed)) {
          this.setQueuedState(true);
        } else if (this.isQueued) {
          this.setQueuedState(false);
          this.setStatus('');
        }
      } catch (_) {
        if (this.queueStatusRequestId === requestId && this.isQueued) {
          this.setQueuedState(false);
          this.setStatus('');
        }
      }
    }

    async handleSubmit(event) {
      event.preventDefault();

      const email = (this.emailInput?.value || '').trim().toLowerCase();
      if (!looksLikeEmail(email)) {
        this.setStatus('Enter a valid email address.', true);
        return;
      }

      if (this.isQueued) {
        this.setQueuedState(true);
        return;
      }

      const payload = this.buildPayload(email);

      this.setStatus('Saving...');
      if (this.button) this.button.disabled = true;
      if (this.buttonText) this.buttonText.textContent = 'Saving...';

      try {
        const response = await fetch(this.dataset.endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        const data = await response.json().catch(() => ({}));

        if (!response.ok || data.success === false) {
          throw new Error(data.message || defaultErrorMessage);
        }

        if (data.pendingConfirmation) {
          this.setStatus(data.message || confirmationMessage);
          if (this.button) this.button.disabled = false;
          if (this.buttonText) this.buttonText.textContent = 'Email sent';
          return;
        }

        this.rememberAccessToken(email, data.accessToken);

        if (data.alreadySubscribed) {
          this.setQueuedState(true);
        } else {
          this.setStatus(successMessage);
          if (this.buttonText) this.buttonText.textContent = 'Saved';
        }
      } catch (error) {
        this.setStatus(error.message || defaultErrorMessage, true);
        this.resetButton();
      }
    }
  }

  if (!customElements.get('cavern-notify-me')) {
    customElements.define('cavern-notify-me', CavernNotifyMe);
  }
})();
